/**
 * ArchLang v0.10 — selector & expression sub-grammar parser (§1–§2).
 *
 * Two mutually-recursive recursive-descent grammars over the shared token
 * stream. They meet ONLY at parenthesised keyword portals: `where (…)`,
 * `exists (…)`, `count (…)`, `<head> of (…)`. Inside a portal the inner
 * grammar starts fresh — `>` is an arrow in a selector portal and a comparison
 * in an expression, `*` is the universal atom vs multiplication. There is no
 * precedence interaction across the wall.
 *
 * `SelectorExprParser` holds a cursor into a token array and appends to a
 * shared error list; the declaration parser (P1a-b) will hand it a token slice
 * for each selector/expression slot and read `pos` back. The standalone
 * `parseSelectorSource` / `parseExpressionSource` entry points tokenise a
 * string and parse a single selector/expression to EOF — the P1a-a fixtures.
 *
 * Precedence:
 *   selector : `or` ⟂ `and` ⟂ `not` ⟂ term(arrow-chain / predicate / primary)
 *   expression : `or` ⟂ `and` ⟂ `not` ⟂ compare ⟂ additive ⟂ multiplicative
 *                ⟂ unary `-` ⟂ primary
 */

import type { Name, QualifiedName, SourceSpan, Value } from "./types.js";
import type { Token, TokenType } from "./lexer.js";
import { RESERVED_KEYWORDS, tokenize } from "./lexer.js";
import type {
  ArrowOp,
  AspectValueKnob,
  BinaryOp,
  Expr,
  ExprGetter,
  ExtractionHead,
  GetterSigil,
  Selector,
  SelHop,
  WithinBound,
} from "./selector-ast.js";
import { SELECTOR_HEAD_KEYWORDS } from "./selector-ast.js";

export interface SelectorParseError {
  readonly message: string;
  readonly span?: SourceSpan;
}

const EXTRACTION_HEADS: ReadonlySet<string> = new Set(["sources", "targets", "nodes", "owners"]);

/**
 * A cursor-carrying parser for the v0.10 selector & expression sub-grammars.
 * Construct over a token array (from `tokenize`), optionally starting at an
 * offset; `pos` advances as productions consume and can be read back by an
 * embedding declaration parser.
 */
export class SelectorExprParser {
  private readonly tokens: readonly Token[];
  pos: number;
  readonly errors: SelectorParseError[] = [];

  constructor(tokens: readonly Token[], start = 0) {
    this.tokens = tokens;
    this.pos = start;
  }

  // ===========================================================================
  // Selector grammar
  // ===========================================================================

  /** Parse a full selector (lowest-precedence `or`). */
  parseSelector(): Selector {
    return this.parseSelOr();
  }

  private parseSelOr(): Selector {
    const start = this.peek();
    let left = this.parseSelAnd();
    if (!this.isKw("or")) return left;
    const terms: Selector[] = [left];
    while (this.matchKw("or")) terms.push(this.parseSelAnd());
    return { kind: "or", terms, source: this.spanFrom(start) };
  }

  private parseSelAnd(): Selector {
    const start = this.peek();
    const left = this.parseSelNot();
    if (!this.isKw("and")) return left;
    const terms: Selector[] = [left];
    while (this.matchKw("and")) terms.push(this.parseSelNot());
    return { kind: "and", terms, source: this.spanFrom(start) };
  }

  private parseSelNot(): Selector {
    if (this.isKw("not")) {
      const start = this.advance();
      const term = this.parseSelNot();
      return { kind: "not", term, source: this.spanFrom(start) };
    }
    return this.parseSelTerm();
  }

  /** A term: a predicate, or a set term (primary optionally followed by arrows). */
  private parseSelTerm(): Selector {
    // Predicates.
    if (this.check("IN")) {
      const start = this.advance();
      const arg = this.parseSelPrimary();
      // `depth N` is a suffix on the target, exactly like `within N` on a hop.
      let depth: WithinBound | undefined;
      if (this.isKw("depth")) {
        this.advance();
        depth = this.parseBound("depth");
      }
      return { kind: "in", arg, depth, source: this.spanFrom(start) };
    }
    // Bare `depth N` — claimed only when a bound actually follows, so a lone
    // `depth` falls through to the primary and gets the reserved-word hint
    // every other term-head keyword gives, not "`depth` needs a number".
    if (this.isKw("depth") && this.boundFollows()) {
      const start = this.advance();
      return { kind: "depth", bound: this.parseBound("depth"), source: this.spanFrom(start) };
    }
    if (this.check("ON")) {
      const start = this.advance();
      const plane = this.parsePlaneName();
      return { kind: "on", plane, source: this.spanFrom(start) };
    }
    if (this.isKw("where")) {
      const start = this.advance();
      this.consume("LPAREN", "`where` requires a parenthesised expression");
      const expr = this.parseExpression();
      this.consume("RPAREN", "Expected ')' to close `where` expression");
      return { kind: "where", expr, source: this.spanFrom(start) };
    }
    // A set term: primary, then any arrow hops (chain).
    const start = this.peek();
    const head = this.parseSelPrimary();
    if (!this.isArrowOp()) return head;
    const hops: SelHop[] = [];
    while (this.isArrowOp()) {
      const opTok = this.advance();
      const op = opTok.value as ArrowOp;
      const rhs = this.parseSelPrimary();
      // `within N` is a suffix on the hop (`X <> Y within 2`), after the RHS.
      let within: WithinBound | undefined;
      if (this.isKw("within")) {
        this.advance();
        within = this.parseBound("within");
      }
      hops.push({ op, rhs, within, source: this.spanFrom(opTok) });
    }
    return { kind: "arrow", head, hops, source: this.spanFrom(start) };
  }

  /**
   * A primary / arrow side: `*`, `this`, `outside`, `$knob`, `@@aspect`,
   * `violating P`, an extraction, a parenthesised selector, or a bare ref path.
   */
  private parseSelPrimary(): Selector {
    const tok = this.peek();

    if (this.check("STAR")) {
      this.advance();
      return { kind: "universal", source: tok.span };
    }
    if (this.check("LPAREN")) {
      this.advance();
      const inner = this.parseSelector();
      this.consume("RPAREN", "Expected ')' to close selector group");
      // Parens are sort-transparent — the tree captures grouping. The SPAN,
      // though, must cover them: the view builder re-emits a clause from the
      // source text its span points at, and an inner-only span rewrote
      // `group by in (A or B)` to `group by in A or B` on every save.
      return { ...inner, source: this.spanFrom(tok) } as Selector;
    }
    if (this.check("DOLLAR")) {
      this.advance();
      const name = this.consume("IDENTIFIER", "Expected a knob name after '$'").value;
      return { kind: "knob", name, source: this.spanFrom(tok) };
    }
    if (this.check("ATAT")) {
      return this.parseAspectAtom();
    }
    // `@deco` / `@deco:"v3"` — decoration CARRIERS, parity with `@@key`. In atom
    // position a single `@` names a decoration; in expression position the same
    // token starts a getter. Position disambiguates, exactly as for `@@`.
    if (this.check("AT")) {
      return this.parseDecorationAtom();
    }
    if (this.isKw("this")) {
      this.advance();
      return { kind: "this", source: tok.span };
    }
    if (this.isKw("outside")) {
      this.advance();
      return { kind: "outside", source: tok.span };
    }
    if (this.isKw("violating")) {
      this.advance();
      const policy = this.parseQualifiedName();
      return { kind: "violating", policy, source: this.spanFrom(tok) };
    }
    if (this.isExtractionHead()) {
      const head = this.advance().value as ExtractionHead;
      this.consumeKw("of", "Expected `of` after extraction head");
      this.consume("LPAREN", "Expected '(' after `of`");
      const inner = this.parseSelector();
      this.consume("RPAREN", "Expected ')' to close extraction");
      return { kind: "extraction", head, inner, source: this.spanFrom(tok) };
    }
    // Keyword-kind atoms: `process` / `subprocess` (structural node-kinds) plus the
    // reserved `type` / `view` heads tokenize as their own keyword tokens, not as
    // IDENTIFIER, so they'd otherwise fall through to "Expected a selector term".
    // In atom-head position they are legal bare atoms denoting a single-segment ref
    // path of the keyword text — the evaluator resolves `process`/`subprocess` as
    // node-kinds (BUILTIN_KINDS) and `type`/`view` as ordinary element refs.
    // Declaration contexts are unaffected: the declaration parser consumes these
    // keywords at its own heads, never inside a selector slot's primary position.
    if (this.check("PROCESS") || this.check("SUBPROCESS") || this.check("TYPE") || this.check("VIEW")) {
      const kw = this.advance();
      return { kind: "ref", path: [kw.value], source: kw.span };
    }
    if (this.check("IDENTIFIER")) {
      // A reserved term-head word with no qualifying path is a diagnostic.
      // (`in`/`on` are lexer keywords — never IDENTIFIER — so their presence in
      // the set is inert here.)
      if (SELECTOR_HEAD_KEYWORDS.has(tok.value) && !this.headHasPath()) {
        this.error(tok, `\`${tok.value}\` is reserved here — qualify the element name (e.g. \`Parent.${tok.value}\`)`);
      }
      const path = this.parseQualifiedName();
      return { kind: "ref", path, source: this.spanFrom(tok) };
    }

    this.error(tok, `Expected a selector term, got '${tok.value || tok.type}'`);
    this.advance();
    return { kind: "ref", path: [""], source: tok.span };
  }

  /** `@@key` / `@@key:"value"` / `@@key:Place` / `@@key:$knob` — the value
   *  glues (no whitespace). */
  private parseAspectAtom(): Selector {
    const at = this.advance(); // ATAT
    const path = this.parseQualifiedName();
    let value: Value | AspectValueKnob | undefined;
    // A glued `:` (no interior whitespace) begins the valued atom; a spaced `:`
    // is the require subject/obligation separator, handled by the caller.
    if (this.check("COLON") && this.glued(this.previous(), this.peek())) {
      const colon = this.advance();
      const vtok = this.peek();
      if (!this.glued(colon, vtok)) {
        this.error(vtok, "Glue the aspect value to the key: `@@key:value`");
      }
      if (this.check("DOLLAR")) {
        // A value-kind knob in the value slot (design §1 node atoms).
        this.advance();
        const knob = this.consume("IDENTIFIER", "Expected a knob name after '$'").value;
        value = { knob, source: this.spanFrom(vtok) };
      } else {
        value = this.parseAtomicValue();
      }
    }
    return { kind: "aspect", path, value, source: this.spanFrom(at) };
  }

  /** `@deco` / `@deco:"v3"` — the decoration analogue of {@link parseAspectAtom},
   *  including the glued-colon rule for the valued form. */
  private parseDecorationAtom(): Selector {
    const at = this.advance(); // AT
    const nameTok = this.consume("IDENTIFIER", "Expected a decoration name after '@'");
    let value: Value | undefined;
    if (this.check("COLON") && this.glued(this.previous(), this.peek())) {
      const colon = this.advance();
      const vtok = this.peek();
      if (!this.glued(colon, vtok)) {
        this.error(vtok, "Glue the decoration value to the name: `@deco:value`");
      }
      value = this.parseAtomicValue();
    }
    return { kind: "decoration", name: nameTok.value, value, source: this.spanFrom(at) };
  }

  /** Plane name — an identifier (open-world; may be a dotted path). */
  private parsePlaneName(): Name {
    const parts = this.parseQualifiedName();
    return parts.join(".");
  }

  /** Does a `within`/`depth` bound (a number or a `$knob`) follow the keyword? */
  private boundFollows(): boolean {
    const next = this.peekN(1);
    return next?.type === "NUMBER" || next?.type === "DOLLAR";
  }

  /** The shared numeric bound of `within N` (hop distance) and `depth N`
   *  (containment levels): a literal number or a `$knob`. */
  private parseBound(kw: "within" | "depth"): WithinBound {
    const tok = this.peek();
    if (this.check("DOLLAR")) {
      this.advance();
      const knob = this.consume("IDENTIFIER", "Expected a knob name after '$'").value;
      return { value: { knob }, source: this.spanFrom(tok) };
    }
    if (this.check("NUMBER")) {
      this.advance();
      return { value: Number(tok.value), source: tok.span };
    }
    this.error(tok, `\`${kw}\` needs a number or \`$knob\``);
    return { value: 0, source: tok.span };
  }

  // ===========================================================================
  // Expression grammar
  // ===========================================================================

  /** Parse a full expression (lowest-precedence `or`). */
  parseExpression(): Expr {
    return this.parseExprOr();
  }

  private parseExprOr(): Expr {
    let left = this.parseExprAnd();
    while (this.isKw("or")) {
      const op = this.advance();
      const right = this.parseExprAnd();
      left = { kind: "binary", op: "or", left, right, source: this.joinSpan(left, right) };
    }
    return left;
  }

  private parseExprAnd(): Expr {
    let left = this.parseExprNot();
    while (this.isKw("and")) {
      this.advance();
      const right = this.parseExprNot();
      left = { kind: "binary", op: "and", left, right, source: this.joinSpan(left, right) };
    }
    return left;
  }

  private parseExprNot(): Expr {
    if (this.isKw("not")) {
      const start = this.advance();
      const operand = this.parseExprNot();
      return { kind: "unary", op: "not", operand, source: this.spanFrom(start) };
    }
    return this.parseExprCompare();
  }

  private parseExprCompare(): Expr {
    let left = this.parseExprAdditive();
    // `<>` in expression context is a targeted "use !=" (symmetric with the
    // selector-side `> X within 3` → "use >>"); recover by treating it as `!=`.
    if (this.check("LTGT")) {
      const tok = this.advance();
      this.error(tok, "`<>` is a selector operator — use `!=` in an expression");
      const right = this.parseExprAdditive();
      return { kind: "binary", op: "!=", left, right, source: this.joinSpan(left, right) };
    }
    const op = this.comparisonOp();
    if (op) {
      this.advance();
      const right = this.parseExprAdditive();
      left = { kind: "binary", op, left, right, source: this.joinSpan(left, right) };
    }
    return left;
  }

  private parseExprAdditive(): Expr {
    let left = this.parseExprMultiplicative();
    for (;;) {
      const op: BinaryOp | undefined = this.check("PLUS") ? "+" : this.check("MINUS") ? "-" : undefined;
      if (!op) break;
      this.advance();
      const right = this.parseExprMultiplicative();
      left = { kind: "binary", op, left, right, source: this.joinSpan(left, right) };
    }
    return left;
  }

  private parseExprMultiplicative(): Expr {
    let left = this.parseExprUnary();
    for (;;) {
      const op: BinaryOp | undefined = this.check("STAR") ? "*" : this.check("SLASH") ? "/" : undefined;
      if (!op) break;
      this.advance();
      const right = this.parseExprUnary();
      left = { kind: "binary", op, left, right, source: this.joinSpan(left, right) };
    }
    return left;
  }

  private parseExprUnary(): Expr {
    if (this.check("MINUS")) {
      const start = this.advance();
      const operand = this.parseExprUnary();
      return { kind: "unary", op: "-", operand, source: this.spanFrom(start) };
    }
    return this.parseExprPrimary();
  }

  private parseExprPrimary(): Expr {
    const tok = this.peek();

    if (this.check("LPAREN")) {
      this.advance();
      const inner = this.parseExpression();
      this.consume("RPAREN", "Expected ')' to close expression");
      return inner;
    }
    if (this.check("ATAT") || this.check("AT")) {
      return this.parseGetter();
    }
    if (this.check("STRING")) {
      this.advance();
      return { kind: "literal", value: { kind: "string", text: tok.value, source: tok.span }, source: tok.span };
    }
    if (this.check("NUMBER")) {
      this.advance();
      return { kind: "literal", value: { kind: "number", value: Number(tok.value), source: tok.span }, source: tok.span };
    }
    if (this.check("TRUE") || this.check("FALSE")) {
      this.advance();
      return { kind: "literal", value: { kind: "boolean", value: tok.type === "TRUE", source: tok.span }, source: tok.span };
    }
    if (this.check("LTGT")) {
      // `<>` in expression context is a targeted "use !=" (symmetric with
      // `> X within 3` → "use >>"). Consume so recovery continues.
      this.advance();
      this.error(tok, "`<>` is a selector operator — use `!=` in an expression");
      return { kind: "literal", value: { kind: "boolean", value: false, source: tok.span }, source: tok.span };
    }
    if (this.check("IDENTIFIER")) {
      const word = tok.value;
      if (word === "this") {
        this.advance();
        return { kind: "this", source: tok.span };
      }
      if (word === "exists") {
        this.advance();
        this.consume("LPAREN", "`exists` requires a parenthesised selector");
        const selector = this.parseSelector();
        this.consume("RPAREN", "Expected ')' to close `exists`");
        return { kind: "exists", selector, source: this.spanFrom(tok) };
      }
      if (word === "count" || word === "sum" || word === "min" || word === "max") {
        return this.parseAggregate(tok);
      }
      // Registered function call: identifier immediately followed by `(`.
      if (this.peekN(1)?.type === "LPAREN") {
        return this.parseFunctionCall(tok);
      }
      // Otherwise a literal identifier (a value, like a string/number).
      this.advance();
      return { kind: "literal", value: { kind: "identifier", text: word, source: tok.span }, source: tok.span };
    }

    this.error(tok, `Expected an expression, got '${tok.value || tok.type}'`);
    this.advance();
    return { kind: "literal", value: { kind: "string", text: "", source: tok.span }, source: tok.span };
  }

  /** `@path`, `@@path`, or a capability `@head(<raw>)`. */
  protected parseGetter(): ExprGetter {
    const sig = this.advance(); // AT | ATAT
    const sigil: GetterSigil = sig.type === "ATAT" ? "@@" : "@";
    const path = this.parseGetterPath();
    // Capability form: a GLUED `(` after the path → raw-scan to the first `)`
    // (the `#id` glued-token precedent — a spaced `(` is not a capability arg).
    if (sigil === "@" && this.check("LPAREN") && this.glued(this.previous(), this.peek())) {
      const capabilityArgs = this.scanRawParens();
      return { kind: "getter", sigil, path, capabilityArgs, source: this.spanFrom(sig) };
    }
    return { kind: "getter", sigil, path, source: this.spanFrom(sig) };
  }

  private parseAggregate(head: Token): Expr {
    this.advance(); // count | sum | min | max
    const op = head.value;
    if (op === "count" && !this.isKw("distinct")) {
      this.consume("LPAREN", "`count` requires a parenthesised selector");
      const selector = this.parseSelector();
      this.consume("RPAREN", "Expected ')' to close `count`");
      return { kind: "aggregate", op: "count", selector, source: this.spanFrom(head) };
    }
    // count distinct @g of (…) | sum|min|max @g of (…)
    const aggOp = op === "count" ? "count distinct" : (op as "sum" | "min" | "max");
    if (op === "count") this.advance(); // distinct
    const getter = this.parseGetter();
    this.consumeKw("of", `Expected \`of\` after \`${aggOp}\` getter`);
    this.consume("LPAREN", "Expected '(' after `of`");
    const selector = this.parseSelector();
    this.consume("RPAREN", "Expected ')' to close aggregate");
    return { kind: "aggregate", op: aggOp, getter, selector, source: this.spanFrom(head) };
  }

  private parseFunctionCall(head: Token): Expr {
    const fn = this.advance().value;
    this.consume("LPAREN", "Expected '(' after function name");
    const args: Expr[] = [];
    if (!this.check("RPAREN")) {
      args.push(this.parseExpression());
      while (this.matchToken("COMMA")) args.push(this.parseExpression());
    }
    this.consume("RPAREN", "Expected ')' to close function call");
    return { kind: "call", fn, args, source: this.spanFrom(head) };
  }

  /**
   * Consume a balanced `( … )` verbatim (capability args, design §5 —
   * "raw-scan to first `)`"), returning the interior text sliced from the
   * original source by offset. `rawSource` must be set (the standalone entry
   * points and the embedding declaration parser both set it).
   */
  private scanRawParens(): string {
    const open = this.consume("LPAREN", "Expected '('");
    let depth = 1;
    const startOffset = open.span.end.offset;
    let endOffset = startOffset;
    while (!this.isAtEnd() && depth > 0) {
      const t = this.peek();
      if (t.type === "LPAREN") depth++;
      else if (t.type === "RPAREN") {
        depth--;
        if (depth === 0) {
          endOffset = t.span.start.offset;
          this.advance();
          break;
        }
      }
      endOffset = t.span.end.offset;
      this.advance();
    }
    return (this.rawSource ?? "").slice(startOffset, endOffset).trim();
  }

  // ===========================================================================
  // Shared leaves
  // ===========================================================================

  /** A dotted identifier path (`security.zone`). */
  protected parseQualifiedName(): QualifiedName {
    const parts: Name[] = [];
    const first = this.consume("IDENTIFIER", "Expected identifier");
    parts.push(first.value);
    while (this.check("DOT")) {
      this.advance();
      parts.push(this.consume("IDENTIFIER", "Expected identifier after '.'").value);
    }
    return parts;
  }

  /**
   * A getter/aspect path (`repo.url`, `security.zone`). After a sigil, path
   * segments are contextual identifiers — a segment may be a reserved keyword
   * (`@type`, `@dist`, `@@in`), which the lexer emitted as a keyword token.
   */
  private parseGetterPath(): QualifiedName {
    const parts: Name[] = [this.consumeWord()];
    while (this.check("DOT")) {
      this.advance();
      parts.push(this.consumeWord());
    }
    return parts;
  }

  /** Consume one path word — an IDENTIFIER or any reserved-keyword token. */
  private consumeWord(): Name {
    const t = this.peek();
    if (t.type === "IDENTIFIER" || (t.value !== "" && RESERVED_KEYWORDS.has(t.value))) {
      this.advance();
      return t.value;
    }
    this.error(t, "Expected a getter path segment");
    return "";
  }

  /** A string or identifier(ref) aspect value. */
  protected parseAtomicValue(): Value {
    const tok = this.peek();
    if (tok.type === "STRING") {
      this.advance();
      return { kind: "string", text: tok.value, source: tok.span };
    }
    if (tok.type === "IDENTIFIER") {
      this.advance();
      return { kind: "identifier", text: tok.value, source: tok.span };
    }
    if (tok.type === "NUMBER") {
      this.advance();
      return { kind: "number", value: Number(tok.value), source: tok.span };
    }
    this.error(tok, "Expected an aspect value (string or place)");
    return { kind: "string", text: "", source: tok.span };
  }

  // ===========================================================================
  // Token helpers
  // ===========================================================================

  protected peek(): Token {
    return this.tokens[this.pos] ?? this.eof();
  }

  protected peekN(n: number): Token | undefined {
    return this.tokens[this.pos + n];
  }

  protected previous(): Token {
    return this.tokens[this.pos - 1] ?? this.peek();
  }

  protected advance(): Token {
    const tok = this.peek();
    if (!this.isAtEnd()) this.pos++;
    return tok;
  }

  protected isAtEnd(): boolean {
    return this.peek().type === "EOF";
  }

  protected check(type: TokenType): boolean {
    return this.peek().type === type;
  }

  protected isKw(word: string): boolean {
    const t = this.peek();
    return t.type === "IDENTIFIER" && t.value === word;
  }

  protected matchKw(word: string): boolean {
    if (this.isKw(word)) {
      this.advance();
      return true;
    }
    return false;
  }

  protected consumeKw(word: string, message: string): void {
    if (this.isKw(word)) {
      this.advance();
      return;
    }
    this.error(this.peek(), message);
  }

  protected matchToken(type: TokenType): Token | null {
    return this.check(type) ? this.advance() : null;
  }

  protected consume(type: TokenType, message: string): Token {
    if (this.check(type)) return this.advance();
    const tok = this.peek();
    this.error(tok, `${message} (got '${tok.value || tok.type}')`);
    return tok;
  }

  private isArrowOp(): boolean {
    return this.check("GT") || this.check("GTGT") || this.check("LTGT");
  }

  private comparisonOp(): BinaryOp | undefined {
    switch (this.peek().type) {
      case "EQUALS":
        return "=";
      case "NE":
        return "!=";
      case "LT":
        return "<";
      case "LE":
        return "<=";
      case "GT":
        return ">";
      case "GE":
        return ">=";
      default:
        return undefined;
    }
  }

  private isExtractionHead(): boolean {
    const t = this.peek();
    return (
      t.type === "IDENTIFIER" &&
      EXTRACTION_HEADS.has(t.value) &&
      this.peekN(1)?.type === "IDENTIFIER" &&
      this.peekN(1)?.value === "of"
    );
  }

  /** Does the current reserved head token continue into a dotted path? */
  private headHasPath(): boolean {
    return this.peekN(1)?.type === "DOT";
  }

  /** True when no trivia at all sits between two adjacent tokens. */
  protected glued(before: Token, after: Token): boolean {
    return before.trailingTrivia.length === 0 && after.leadingTrivia.length === 0;
  }

  protected spanFrom(start: Token): SourceSpan {
    const end = this.previous();
    return { start: start.span.start, end: end.span.end, file: start.span.file };
  }

  private joinSpan(a: { source?: SourceSpan }, b: { source?: SourceSpan }): SourceSpan | undefined {
    if (!a.source || !b.source) return a.source ?? b.source;
    return { start: a.source.start, end: b.source.end, file: a.source.file };
  }

  /** Raw source, set by the entry points so capability args can be raw-scanned. */
  rawSource?: string;

  private eof(): Token {
    const last = this.tokens[this.tokens.length - 1];
    return (
      last ?? {
        type: "EOF",
        value: "",
        span: { start: { line: 1, column: 1, offset: 0 }, end: { line: 1, column: 1, offset: 0 } },
        leadingTrivia: [],
        trailingTrivia: [],
      }
    );
  }

  protected error(token: Token, message: string): void {
    this.errors.push({ message, span: token.span });
  }
}

export interface SelectorParseResult {
  readonly selector: Selector;
  readonly errors: readonly SelectorParseError[];
}

export interface ExpressionParseResult {
  readonly expr: Expr;
  readonly errors: readonly SelectorParseError[];
}

/** Tokenise `src` and parse a single selector to EOF (P1a-a fixtures). */
export function parseSelectorSource(src: string, file?: string): SelectorParseResult {
  const lex = tokenize(src, file);
  const parser = new SelectorExprParser(lex.tokens);
  parser.rawSource = src;
  const selector = parser.parseSelector();
  const errors: SelectorParseError[] = [
    ...lex.errors.map((e) => ({ message: e.message, span: e.span })),
    ...parser.errors,
  ];
  if (parser.pos < lex.tokens.length - 1) {
    errors.push({ message: `Unexpected trailing input in selector`, span: lex.tokens[parser.pos]?.span });
  }
  return { selector, errors };
}

/** Tokenise `src` and parse a single expression to EOF (P1a-a fixtures). */
export function parseExpressionSource(src: string, file?: string): ExpressionParseResult {
  const lex = tokenize(src, file);
  const parser = new SelectorExprParser(lex.tokens);
  parser.rawSource = src;
  const expr = parser.parseExpression();
  const errors: SelectorParseError[] = [
    ...lex.errors.map((e) => ({ message: e.message, span: e.span })),
    ...parser.errors,
  ];
  if (parser.pos < lex.tokens.length - 1) {
    errors.push({ message: `Unexpected trailing input in expression`, span: lex.tokens[parser.pos]?.span });
  }
  return { expr, errors };
}
